import { useEffect } from 'react';

const SITE_URL = 'https://solvetax.in';
const DEFAULT_TITLE = 'SolveTax | Online Income Tax Filing, GST, Company Registration in India';
const DEFAULT_DESCRIPTION =
  "SolveTax is India's trusted CA-led platform for Income Tax Return (ITR) filing, GST registration & filing, Pvt Ltd / LLP company incorporation, MCA ROC compliance, payroll, trademark and MSME services. Trusted by 5000+ Indian taxpayers in Hindi, Telugu and English.";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Sets per-page title, meta description, canonical URL, Open Graph tags and
 * JSON-LD structured data (e.g. FAQPage, BreadcrumbList) for this SPA — and
 * reverts to the site-wide defaults on unmount so navigating away doesn't
 * leave stale tags on the next page.
 */
export function useDocumentHead({ title, description, path, jsonLd } = {}) {
  useEffect(() => {
    const resolvedTitle = title || DEFAULT_TITLE;
    const resolvedDescription = description || DEFAULT_DESCRIPTION;
    const resolvedUrl = path ? `${SITE_URL}${path}` : `${SITE_URL}/`;

    document.title = resolvedTitle;
    upsertMeta('name', 'description', resolvedDescription);
    upsertMeta('property', 'og:title', resolvedTitle);
    upsertMeta('property', 'og:description', resolvedDescription);
    upsertMeta('property', 'og:url', resolvedUrl);
    upsertLink('canonical', resolvedUrl);

    const entries = Object.entries(jsonLd || {});
    entries.forEach(([id, data]) => upsertJsonLd(`ld-${id}`, data));

    return () => {
      document.title = DEFAULT_TITLE;
      upsertMeta('name', 'description', DEFAULT_DESCRIPTION);
      upsertMeta('property', 'og:title', DEFAULT_TITLE);
      upsertMeta('property', 'og:description', DEFAULT_DESCRIPTION);
      upsertMeta('property', 'og:url', `${SITE_URL}/`);
      upsertLink('canonical', `${SITE_URL}/`);
      entries.forEach(([id]) => upsertJsonLd(`ld-${id}`, null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, jsonLd]);
}

export { SITE_URL };
